import EmblaCarousel from "//pierrebassene.world/cdn/shop/t/109/assets/m-carousel.js?v=64673680518834481301758992299";
import ClassNames from "//pierrebassene.world/cdn/shop/t/109/assets/h-carousel-cn.js?v=158060002466198821461758992303";

const MOBILE_MQ = "(max-width: 768px)";

const filterShopifyEvent = (event, element, callback) => {
  if (event.target.contains(element) || element.contains(event.target)) {
    callback(event);
  }
};

if (!customElements.get("blog-grid-carousel")) {
  customElements.define(
    "blog-grid-carousel",
    class BlogGridCarousel extends HTMLElement {
      constructor() {
        super();
        this.embla = null;
        this.autoplayTimer = null;
        this.isPaused = false;
        this.onMediaChange = null;
      }

      readSettings() {
        this.autoplayEnabled =
          this.getAttribute("data-enable-autoplay") !== "false" && this.hasAttribute("autoplay");
        this.infiniteLoopEnabled =
          this.getAttribute("data-infinite-loop") !== "false" && this.hasAttribute("infinite-loop");
        this.autoplayDelay = parseInt(this.getAttribute("delay") || "4000", 10);
        this.scrollDirection =
          this.getAttribute("scroll-direction") === "reverse" ? "reverse" : "forward";
        this.slidesDesktop = parseInt(this.getAttribute("data-slides-desktop") || "2", 10);
        this.slidesMobile = parseInt(this.getAttribute("data-slides-mobile") || "1", 10);
      }

      connectedCallback() {
        this.viewportNode = this.querySelector("[slider-viewport]");
        this.trackNode = this.querySelector("[slider-container]");
        if (!this.viewportNode || !this.trackNode) return;

        this.readSettings();
        this.prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        this.onMediaChange = () => this.initCarousel();
        window.matchMedia(MOBILE_MQ).addEventListener("change", this.onMediaChange);

        this.initCarousel();
        this.setupEditorSupport();
      }

      disconnectedCallback() {
        this.stopAutoplay();
        this.embla?.destroy();
        this.embla = null;

        if (this.onMediaChange) {
          window.matchMedia(MOBILE_MQ).removeEventListener("change", this.onMediaChange);
        }

        if (this.onSectionLoad) {
          document.removeEventListener("shopify:section:load", this.onSectionLoad);
          document.removeEventListener("shopify:block:select", this.onSectionLoad);
        }
      }

      getOriginalSlides() {
        return [...this.trackNode.querySelectorAll("[slider-slide]:not([data-embla-clone])")];
      }

      getSlideCount() {
        return this.getOriginalSlides().length;
      }

      getSlidesPerView() {
        const isMobile = window.matchMedia(MOBILE_MQ).matches;
        return isMobile ? this.slidesMobile : this.slidesDesktop;
      }

      shouldUseCarousel() {
        return this.getSlideCount() > this.getSlidesPerView();
      }

      setStaticGridMode(isStatic) {
        this.classList.toggle("is-static-grid", isStatic);

        if (isStatic) {
          this.setAttribute("data-carousel-active", "false");
        } else {
          this.setAttribute("data-carousel-active", "true");
        }
      }

      removeClonedSlides() {
        this.trackNode.querySelectorAll("[slider-slide][data-embla-clone]").forEach((slide) => {
          slide.remove();
        });
      }

      bindEmblaEvents() {
        if (!this.embla) return;

        this.embla.on("init", this.handleA11y.bind(this));
        this.embla.on("select", this.handleA11y.bind(this));
        this.embla.on("reInit", this.handleA11y.bind(this));
        this.embla.on("pointerDown", this.handlePointerDown.bind(this));
        this.embla.on("pointerUp", this.handlePointerUp.bind(this));
      }

      createEmbla(shouldLoop) {
        return EmblaCarousel(
          this.viewportNode,
          {
            loop: shouldLoop,
            align: "start",
            dragFree: false,
            containScroll: shouldLoop ? false : "trimSnaps",
            slidesToScroll: 1,
          },
          [ClassNames()]
        );
      }

      initCarousel() {
        const slideCount = this.getSlideCount();
        const useCarousel = this.shouldUseCarousel();

        this.embla?.destroy();
        this.embla = null;
        this.stopAutoplay();
        this.removeClonedSlides();

        if (!useCarousel) {
          this.setStaticGridMode(true);
          this.handleStaticA11y();
          return;
        }

        this.setStaticGridMode(false);

        const shouldLoop = false;
        this.embla = this.createEmbla(shouldLoop);
        this.bindEmblaEvents();

        if (this.autoplayEnabled && !this.prefersReducedMotion) {
          this.startAutoplay();
        }
      }

      handleStaticA11y() {
        const label = this.getAttribute("aria-label") || "Blog posts";
        const slides = this.querySelectorAll("[slider-slide]");

        this.viewportNode.removeAttribute("role");
        this.viewportNode.removeAttribute("aria-roledescription");
        this.viewportNode.setAttribute("aria-label", label);

        slides.forEach((slide) => {
          slide.removeAttribute("aria-hidden");
          slide.removeAttribute("role");
          slide.removeAttribute("aria-roledescription");
        });
      }

      handlePointerDown() {
        this.isPaused = true;
        this.stopAutoplay();
      }

      handlePointerUp() {
        this.isPaused = false;
        if (this.autoplayEnabled && !this.prefersReducedMotion && this.embla) {
          this.startAutoplay();
        }
      }

      scrollByDirection() {
        if (!this.embla) return;

        if (this.scrollDirection === "reverse") {
          if (this.embla.canScrollPrev()) {
            this.embla.scrollPrev();
            return;
          }

          this.scrollDirection = "forward";
          if (this.embla.canScrollNext()) {
            this.embla.scrollNext();
          }
          return;
        }

        if (this.embla.canScrollNext()) {
          this.embla.scrollNext();
          return;
        }

        this.scrollDirection = "reverse";
        if (this.embla.canScrollPrev()) {
          this.embla.scrollPrev();
        }
      }

      startAutoplay() {
        this.stopAutoplay();

        this.autoplayTimer = window.setInterval(() => {
          if (!this.embla || this.isPaused) return;
          this.scrollByDirection();
        }, this.autoplayDelay);
      }

      stopAutoplay() {
        if (this.autoplayTimer) {
          window.clearInterval(this.autoplayTimer);
          this.autoplayTimer = null;
        }
      }

      handleA11y() {
        if (!this.embla) return;

        const slides = this.embla.slideNodes();
        const inView = this.embla.slidesInView();
        const label = this.getAttribute("aria-label") || "Blog posts carousel";

        this.viewportNode.setAttribute("role", "region");
        this.viewportNode.setAttribute("aria-roledescription", "carousel");
        this.viewportNode.setAttribute("aria-label", label);

        slides.forEach((slide, index) => {
          slide.setAttribute("role", "group");
          slide.setAttribute("aria-roledescription", "slide");

          if (inView.includes(index)) {
            slide.removeAttribute("aria-hidden");
          } else {
            slide.setAttribute("aria-hidden", "true");
          }
        });
      }

      setupEditorSupport() {
        if (!window.Shopify?.designMode) return;

        this.onSectionLoad = (event) => {
          filterShopifyEvent(event, this, () => {
            this.readSettings();
            this.initCarousel();
          });
        };

        document.addEventListener("shopify:section:load", this.onSectionLoad);
        document.addEventListener("shopify:block:select", this.onSectionLoad);
      }
    }
  );
}
